export class HideEmptySections {
  /**
   * Creates a new instance of a HideEmptySections component
   * Hides <section> elements that render with no real height (e.g. an empty placeholder)
   * to prevent the surrounding layout from getting an unwanted margin/gap.
   * Reusable: attach it to any container and it will manage every <section> inside it,
   * regardless of the intermediate markup/class names.
   * @param {HTMLElement} element The container DOM element to scope the lookup to
   */
  constructor(element) {
    this.el = element;
    this._refs = {
      sections: this.el.querySelectorAll(':scope section'),
    };

    // MutationObserver catches DOM structure changes (e.g. lazy-loaded content)
    // regardless of the section's current visibility (display:none reports 0
    // height either way, so we re-measure on every relevant mutation).
    this._mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              const section = mutation.target.closest?.('section');
              if (section) this.toggleSection(section);
            });
          })
        : null;

    this.init();
  }

  init() {
    this._refs.sections.forEach((section) => {
      this.toggleSection(section);

      if (this._mutationObserver) {
        this._mutationObserver.observe(section, {
          childList: true,
          subtree: true,
        });
      }
    });
  }

  /**
   * Adds/removes the .is-hidden class based on whether the section renders with
   * any real height. CSS cannot measure height, so this must be done in JS.
   * @param {HTMLElement} section The <section> element to evaluate
   */
  toggleSection(section) {
    const isHidden = section.classList.contains('is-hidden');

    // Temporarily remove is-hidden to get the real height of the content,
    // otherwise display:none would always report 0, even if content was added.
    if (isHidden) {
      section.classList.remove('is-hidden');
    }

    const hasHeight = section.offsetHeight > 1;

    section.classList.toggle('is-hidden', !hasHeight);
  }

  /**
   * Stops observing and releases the MutationObserver.
   */
  destroy() {
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
  }
}
